import React, { useEffect, useMemo, useState } from 'react';
import { BayenahLogo, LOGO_SIZES } from '@/components/brand/BayenahLogo';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'bayenah-legal-language';

export const TermsPage = () => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ar' || saved === 'en') {
      setLanguage(saved);
      return;
    }
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    setLanguage(browserLang.toLowerCase().startsWith('ar') ? 'ar' : 'en');
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const isArabic = language === 'ar';

  const content = useMemo(() => ({
    en: {
      metaTitle: 'Bayenah Terms of Service',
      metaDescription:
        'Read the terms and conditions for using Bayenah, including user responsibilities and service limitations.',
      title: 'Bayenah Terms of Service',
      lastUpdated: 'Last updated: 24 Feb 2026',
      sections: [
        {
          title: '1) Acceptance of Terms',
          paragraphs: [
            'By accessing or using bayenah.com (the “Service”), you agree to these Terms of Service (“Terms”). If you do not agree, do not use the Service.',
          ],
        },
        {
          title: '2) Eligibility',
          paragraphs: ['You must be at least 13 years old to use the Service.'],
        },
        {
          title: '3) Accounts and Authentication',
          paragraphs: ['You are responsible for maintaining the confidentiality of your account and for all activity under your account. If you believe your account has been compromised, contact us immediately.'],
        },
        {
          title: '4) Permitted Use',
          paragraphs: ['You agree to use the Service lawfully and not to:'],
          list: [
            'Interfere with or disrupt the Service',
            'Attempt unauthorized access to systems or accounts',
            'Upload or transmit malicious code',
            'Use the Service for unlawful, harmful, or abusive activities',
          ],
        },
        {
          title: '5) Content and User Inputs',
          paragraphs: ['You retain rights to the content you submit where applicable. You grant Bayenah a limited right to process your content only to provide and improve the Service.'],
        },
        {
          title: '6) Intellectual Property',
          paragraphs: ['The Service, including its design, branding, and software, is owned by Bayenah or its licensors and is protected by applicable laws.'],
        },
        {
          title: '7) Third-Party Services',
          paragraphs: ['The Service may rely on third-party services (e.g., Google sign-in, hosting). We are not responsible for third-party services beyond our control.'],
        },
        {
          title: '8) Disclaimers',
          paragraphs: ['The Service is provided “as is” and “as available” without warranties of any kind to the maximum extent permitted by law.'],
        },
        {
          title: '9) Limitation of Liability',
          paragraphs: ['To the maximum extent permitted by law, Bayenah will not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or revenues.'],
        },
        {
          title: '10) Termination',
          paragraphs: ['We may suspend or terminate access to the Service if you violate these Terms or if required to protect the Service and users.'],
        },
        {
          title: '11) Changes to Terms',
          paragraphs: ['We may update these Terms from time to time. We will update the “Last updated” date and may provide additional notice if changes are significant.'],
        },
        {
          title: '12) Contact',
          paragraphs: ['For questions about these Terms, contact:', 'Email:'],
        },
      ],
      email: 'hello@bayenah.com',
    },
    ar: {
      metaTitle: 'شروط الاستخدام - بَيّنَة',
      metaDescription:
        'اطّلع على شروط وأحكام استخدام بَيّنَة، بما في ذلك مسؤوليات المستخدم وحدود الخدمة.',
      title: 'شروط الاستخدام – بَيّنَة',
      lastUpdated: 'آخر تحديث: 24 فبراير 2026',
      sections: [
        {
          title: '1) الموافقة على الشروط',
          paragraphs: ['باستخدامك لموقع bayenah.com (“الخدمة”) فإنك توافق على شروط الاستخدام هذه (“الشروط”). إذا لم توافق، يرجى عدم استخدام الخدمة.'],
        },
        {
          title: '2) الأهلية',
          paragraphs: ['يجب ألا يقل عمرك عن 13 عامًا لاستخدام الخدمة.'],
        },
        {
          title: '3) الحسابات وتسجيل الدخول',
          paragraphs: ['أنت مسؤول عن الحفاظ على سرية حسابك وعن جميع الأنشطة التي تتم عبر حسابك. إذا اشتبهت بوجود اختراق، تواصل معنا فورًا.'],
        },
        {
          title: '4) الاستخدام المسموح',
          paragraphs: ['توافق على استخدام الخدمة بشكل نظامي، وألا تقوم بـ:'],
          list: [
            'تعطيل الخدمة أو التدخل في عملها',
            'محاولة الوصول غير المصرّح به للأنظمة أو الحسابات',
            'رفع أو إرسال برمجيات خبيثة',
            'استخدام الخدمة لأغراض غير قانونية أو ضارة أو مسيئة',
          ],
        },
        {
          title: '5) المحتوى ومدخلات المستخدم',
          paragraphs: ['تحتفظ بحقوقك في المحتوى الذي تقدمه عند الاقتضاء. وتمنح “بَيّنَة” حقًا محدودًا لمعالجة المحتوى فقط لتقديم الخدمة وتحسينها.'],
        },
        {
          title: '6) الملكية الفكرية',
          paragraphs: ['الخدمة بما في ذلك التصميم والهوية والبرمجيات مملوكة لـ “بَيّنَة” أو للجهات المرخِّصة لها ومحميّة بموجب الأنظمة ذات الصلة.'],
        },
        {
          title: '7) خدمات الطرف الثالث',
          paragraphs: ['قد تعتمد الخدمة على خدمات طرف ثالث (مثل تسجيل الدخول عبر Google أو الاستضافة). نحن غير مسؤولين عن خدمات الطرف الثالث الخارجة عن سيطرتنا.'],
        },
        {
          title: '8) إخلاء المسؤولية',
          paragraphs: ['تُقدَّم الخدمة “كما هي” و”حسب التوفر” دون أي ضمانات، إلى أقصى حد يسمح به النظام.'],
        },
        {
          title: '9) حدود المسؤولية',
          paragraphs: ['إلى أقصى حد يسمح به النظام، لا تتحمل “بَيّنَة” المسؤولية عن الأضرار غير المباشرة أو العرضية أو الخاصة أو التبعية أو العقابية، أو أي فقدان للبيانات أو الأرباح أو الإيرادات.'],
        },
        {
          title: '10) الإنهاء',
          paragraphs: ['قد نقوم بتعليق أو إنهاء الوصول للخدمة إذا خالفت هذه الشروط أو إذا كان ذلك ضروريًا لحماية الخدمة والمستخدمين.'],
        },
        {
          title: '11) التغييرات على الشروط',
          paragraphs: ['قد نقوم بتحديث هذه الشروط من وقت لآخر. سنقوم بتحديث تاريخ “آخر تحديث”، وقد نقدم إشعارًا إضافيًا إذا كانت التغييرات جوهرية.'],
        },
        {
          title: '12) التواصل',
          paragraphs: ['للاستفسارات حول هذه الشروط:', 'البريد الإلكتروني:'],
        },
      ],
      email: 'hello@bayenah.com',
    },
  }), []);

  const t = content[language] || content.en;

  useEffect(() => {
    document.title = t.metaTitle;
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute('content', t.metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      meta.setAttribute('content', t.metaDescription);
      document.head.appendChild(meta);
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://bayenah.com/terms');
  }, [t]);

  const handleLanguageChange = (value) => {
    setLanguage(value);
    localStorage.setItem(STORAGE_KEY, value);
  };

  return (
    <div
      className="min-h-screen geometric-bg text-[var(--text-primary)]"
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="terms-page"
    >
      <div className="absolute inset-0 geometric-pattern opacity-40 pointer-events-none" />
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-md" data-testid="terms-header">
          <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4">
            <a href="/" className="hover:opacity-80 transition-opacity" data-testid="terms-home-link">
              <BayenahLogo height={LOGO_SIZES.headerMobile} />
            </a>
            <div className="flex items-center gap-2 text-xs font-semibold">
              <button
                type="button"
                onClick={() => handleLanguageChange('en')}
                className={cn(
                  'px-2 py-1 rounded-md border border-white/10',
                  language === 'en' ? 'bg-white/10 text-white' : 'text-[var(--text-secondary)]'
                )}
                data-testid="terms-toggle-en"
              >
                EN
              </button>
              <span className="text-[var(--text-secondary)]">|</span>
              <button
                type="button"
                onClick={() => handleLanguageChange('ar')}
                className={cn(
                  'px-2 py-1 rounded-md border border-white/10',
                  language === 'ar' ? 'bg-white/10 text-white' : 'text-[var(--text-secondary)]',
                  'font-arabic'
                )}
                data-testid="terms-toggle-ar"
              >
                العربية
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="glass-panel-strong rounded-2xl p-6 md:p-10" data-testid="terms-content">
              <h1
                className={cn(
                  'text-2xl md:text-3xl font-bold text-white mb-2',
                  isArabic && 'font-arabic'
                )}
                data-testid="terms-title"
              >
                {t.title}
              </h1>
              <p className={cn('text-sm text-[var(--text-secondary)] mb-8', isArabic && 'font-arabic')} data-testid="terms-last-updated">
                {t.lastUpdated}
              </p>

              <div className="space-y-8">
                {t.sections.map((section, index) => (
                  <section key={section.title} data-testid={`terms-section-${index + 1}`}>
                    <h2
                      className={cn('text-lg font-semibold text-white mb-3', isArabic && 'font-arabic')}
                      data-testid={`terms-section-${index + 1}-title`}
                    >
                      {section.title}
                    </h2>
                    {section.paragraphs?.map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className={cn('text-[var(--text-secondary)] leading-relaxed mb-3', isArabic && 'font-arabic')}
                        data-testid={`terms-section-${index + 1}-paragraph-${pIndex + 1}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {section.list && (
                      <ul
                        className={cn(
                          'space-y-2 text-[var(--text-secondary)] leading-relaxed',
                          isArabic ? 'pr-5 list-disc' : 'pl-5 list-disc',
                          isArabic && 'font-arabic'
                        )}
                        data-testid={`terms-section-${index + 1}-list`}
                      >
                        {section.list.map((item, listIndex) => (
                          <li key={listIndex} data-testid={`terms-section-${index + 1}-list-item-${listIndex + 1}`}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.title === '12) Contact' && (
                      <p className={cn('text-[var(--text-secondary)] leading-relaxed mt-2', isArabic && 'font-arabic')}>
                        <a
                          href={`mailto:${t.email}`}
                          className="text-[var(--accent)] hover:text-white transition-colors"
                          data-testid="terms-contact-email"
                        >
                          {t.email}
                        </a>
                      </p>
                    )}
                    {section.title === '12) التواصل' && (
                      <p className={cn('text-[var(--text-secondary)] leading-relaxed mt-2', isArabic && 'font-arabic')}>
                        <a
                          href={`mailto:${t.email}`}
                          className="text-[var(--accent)] hover:text-white transition-colors"
                          data-testid="terms-contact-email"
                        >
                          {t.email}
                        </a>
                      </p>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>

        <footer className="border-t border-white/10 bg-black/30 px-4 py-6" data-testid="terms-footer">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-3 text-xs text-[var(--text-secondary)]">
            <span data-testid="terms-footer-copy">© 2026 {isArabic ? 'بَيّنَة' : 'Bayenah'}</span>
            <span>•</span>
            <a
              href="/privacy"
              className="hover:text-white transition-colors"
              data-testid="terms-footer-privacy-link"
            >
              {isArabic ? 'الخصوصية' : 'Privacy'}
            </a>
            <span>•</span>
            <a
              href="/terms"
              className="hover:text-white transition-colors"
              data-testid="terms-footer-terms-link"
            >
              {isArabic ? 'الشروط' : 'Terms'}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};
